import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
  useCallback
} from "react"
import { createInitialModalState } from "./modals/factories/modalFactory"
import { ModalType, ModalsContextState, GetModalParams } from "./modals/types"

const ModalContext = createContext<ModalsContextState | null>(null)
export { ModalContext }

export type { ModalType, GetModalParams } from "./modals/types"

export const useModal = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider")
  }

  const { modals, openModal, closeModal, getModalParams } = context

  const legacyOpenModal = ({
    type,
    params
  }: {
    type: ModalType
    params?: any
  }) => {
    openModal(type, params)
  }

  const legacyParams = Object.keys(modals).reduce((acc, modalType) => {
    const params = getModalParams(modalType as ModalType)
    if (params !== undefined) {
      acc[modalType as keyof typeof acc] = params
    }
    return acc
  }, {} as any)

  return {
    modals,
    closeModal,
    openModal: legacyOpenModal,
    params: legacyParams
  }
}

type ModalProviderProps = {
  children: ReactNode
}

export const ModalProvider: FC<ModalProviderProps> = ({ children }) => {
  const [modals, setModals] = useState(() => createInitialModalState())
  const [modalParams, setModalParams] = useState<Record<string, any>>({})

  const openModal = useCallback(
    <T extends ModalType>(type: T, params?: GetModalParams<T>) => {
      // if (!canOpenModal(type, modals)) {
      //   console.warn(
      //     `Cannot open modal ${String(type)} - blocked by another modal`,
      //   )
      //   return
      // }

      // logModalAction("open", type, params)
      setModals((prev) => ({ ...prev, [type]: true }))

      if (params !== undefined) {
        setModalParams((prev: Record<string, any>) => ({
          ...prev,
          [type]: params
        }))
      }
    },
    []
  )

  const closeModal = useCallback((type: ModalType, onClose?: () => void) => {
    if (type === "globalError") return

    // logModalAction("close", type)
    onClose?.()
    setModals((prev) => ({ ...prev, [type]: false }))
    setModalParams((prev: Record<string, any>) => {
      const newParams = { ...prev }
      delete newParams[String(type)]
      return newParams
    })
  }, [])

  const getModalParams = useCallback(
    <T extends ModalType>(type: T): GetModalParams<T> => {
      return modalParams[String(type)] as GetModalParams<T>
    },
    [modalParams]
  )

  const getModalParamsRequired = useCallback(
    <T extends ModalType>(type: T): NonNullable<GetModalParams<T>> => {
      const params = modalParams[String(type)]
      if (params === undefined) {
        throw new Error(
          `Modal ${String(type)} requires parameters but none were provided`
        )
      }
      return params as NonNullable<GetModalParams<T>>
    },
    [modalParams]
  )

  const contextValue: ModalsContextState = {
    modals,
    openModal,
    closeModal,
    getModalParams,
    getModalParamsRequired
  }

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
    </ModalContext.Provider>
  )
}
